import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminHeader from "../../components/layout/AdminHeader";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Clock, FileText } from "lucide-react";

const activityIcons = {
  LeadCreated: "●", InquirySubmitted: "✉", StageChanged: "→", ConsultationRequested: "🗓",
  ConsultationCompleted: "✔", TaskAssigned: "☑", OpportunityViewed: "◇",
  ResourceDownloaded: "⤓",
  AuriConversationStarted: "◈", AuriLeadQualified: "◆", Contact: "◦", note: "•", Archived: "⌇",
};

export default function AdminLeadDetail() {
  const { id } = useParams();
  const settings = useSettings();
  const stages = settings?.get("pipeline_stage") || [];
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/leads/${id}`);
    setLead(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const changeStage = async (stage) => {
    try {
      await api.post(`/leads/${id}/move`, { pipeline_stage: stage });
      toast.success("Stage updated");
      load();
    } catch { toast.error("Failed to update stage"); }
  };

  const addNote = async () => {
    if (!note.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/leads/${id}/activities`, { type: "note", payload: { text: note } });
      setNote("");
      toast.success("Note added");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!lead) return <div className="p-8 text-sm text-altura-charcoal/60">Loading…</div>;

  return (
    <div data-testid={`page-admin-lead-${id}`}>
      <AdminHeader breadcrumb="CRM · Investor" title={lead.name || "Lead"} subtitle={lead.email || lead.phone || ""}
        actions={<Link to="/altura-command/leads" className="btn-ghost-espresso"><ArrowLeft className="h-3.5 w-3.5" /> Back</Link>} />
      <div className="p-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="admin-card">
            <div className="font-editorial text-xl text-altura-espresso">Overview</div>
            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <Field label="Email" value={lead.email} />
              <Field label="Phone" value={lead.phone} />
              <Field label="Country" value={lead.country} />
              <Field label="Preferred contact" value={lead.preferred_contact} />
              <Field label="Primary market" value={lead.primary_market} />
              <Field label="Secondary market" value={lead.secondary_market} />
              <Field label="Investment category" value={lead.investment_category} />
              <Field label="Budget" value={lead.budget_range} />
              <Field label="Timeline" value={lead.timeline} />
              <Field label="Source" value={lead.source} />
              <Field label="Heard about" value={lead.heard_about} />
              <Field label="Created" value={new Date(lead.created_at).toLocaleString()} />
            </div>
            {lead.message && (
              <div className="mt-5 p-4 rounded bg-altura-ivory-2 text-sm text-altura-charcoal/80 whitespace-pre-line" data-testid="lead-message">
                <div className="text-[10px] uppercase tracking-widest text-altura-charcoal/50 mb-1">Message</div>
                {lead.message}
              </div>
            )}
            {lead.referral_code && (
              <div className="mt-5 p-4 rounded border border-altura-gold/40 bg-altura-gold/5 text-sm" data-testid="lead-referral-attribution">
                <div className="text-[10px] uppercase tracking-widest text-altura-gold-dark mb-1">Attributed via The Global Link</div>
                <div className="font-mono text-altura-espresso">{lead.referral_code}</div>
              </div>
            )}
          </div>

          {/* Resource Intelligence — surfaces which digital-library resources
              this lead has requested/downloaded so advisors have an instant
              read on where their curiosity sits. */}
          <ResourceIntelligenceTile lead={lead} />

          <div className="admin-card">
            <div className="flex items-center justify-between">
              <div className="font-editorial text-xl text-altura-espresso">Activity timeline</div>
              <span className="text-xs text-altura-charcoal/50">{lead.activities?.length || 0} events</span>
            </div>
            <div className="mt-5 space-y-4">
              {(lead.activities || []).map((a) => (
                <div key={a.id} className="flex gap-3 text-sm" data-testid={`activity-${a.type}`}>
                  <div className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-altura-gold/15 flex items-center justify-center text-altura-gold-dark font-mono">{activityIcons[a.type] || "•"}</div>
                  <div className="flex-1">
                    <div className="text-altura-espresso font-medium">{a.type}</div>
                    {a.payload?.text && <div className="text-altura-charcoal/75 mt-0.5">{a.payload.text}</div>}
                    {a.payload?.to && <div className="text-altura-charcoal/60 mt-0.5">Moved {a.payload.from || "—"} → {a.payload.to}</div>}
                    {a.payload?.opportunity_title && <div className="text-altura-charcoal/60 mt-0.5">Opportunity: {a.payload.opportunity_title}</div>}
                    {a.payload?.resource_title && <div className="text-altura-charcoal/60 mt-0.5">Resource: {a.payload.resource_title}</div>}
                    {a.payload?.market && <div className="text-altura-charcoal/60 mt-0.5">Market: {a.payload.market}</div>}
                    <div className="mt-1 text-[11px] font-mono text-altura-charcoal/45"><Clock className="inline h-3 w-3 mr-1" />{new Date(a.at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
              {(!lead.activities || lead.activities.length === 0) && <div className="text-xs text-altura-charcoal/50">No activity yet.</div>}
            </div>
          </div>

          <div className="admin-card">
            <div className="font-editorial text-xl text-altura-espresso">Add a note</div>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                      className="admin-input mt-4" placeholder="Contact summary, meeting notes, next action…" data-testid="lead-note-input" />
            <div className="mt-3 flex gap-2">
              <button onClick={addNote} disabled={busy || !note.trim()} className="btn-espresso disabled:opacity-60" data-testid="lead-note-submit">Save note</button>
              <button onClick={async () => { await api.post(`/leads/${id}/activities`, { type: "Contact", payload: { text: "Contact logged" } }); toast.success("Contact logged"); load(); }}
                      className="btn-ghost-espresso" data-testid="lead-log-contact"><MessageSquare className="h-3.5 w-3.5" /> Log contact</button>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="admin-card">
            <div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/50">Pipeline stage</div>
            <select value={lead.pipeline_stage} onChange={(e) => changeStage(e.target.value)} className="admin-select mt-2" data-testid="lead-stage-select">
              {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/50">Priority</div>
            <select className="admin-select mt-2" value={lead.priority || "medium"}
                    onChange={async (e) => { await api.patch(`/leads/${id}`, { priority: e.target.value }); load(); }}
                    data-testid="lead-priority-select">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-altura-charcoal/50">{label}</div>
      <div className="mt-0.5 text-altura-charcoal/85">{value || "—"}</div>
    </div>
  );
}


/**
 * Resource Intelligence Tile — reads the lead's activity timeline for
 * ResourceDownloaded events, deduplicates by resource_key, and renders a
 * summary tile so advisors see what content the lead has already consumed.
 * Falls back to lead.tags matching `resource:*` when the timeline is empty.
 */
function ResourceIntelligenceTile({ lead }) {
  const events = (lead?.activities || []).filter((a) => a.type === "ResourceDownloaded");
  const seen = new Map();
  events.forEach((a) => {
    const key = a.payload?.resource_key;
    if (!key) return;
    if (!seen.has(key)) {
      seen.set(key, { key, title: a.payload?.resource_title || key, at: a.at, count: 1 });
    } else {
      const v = seen.get(key); v.count += 1;
      if (a.at > v.at) v.at = a.at;
    }
  });
  // Fallback from tags if no activities recorded yet (e.g., legacy leads).
  if (seen.size === 0) {
    (lead?.tags || []).forEach((t) => {
      if (typeof t === "string" && t.startsWith("resource:")) {
        const key = t.slice(9);
        seen.set(key, { key, title: key.replace(/_/g, " "), at: null, count: 1 });
      }
    });
  }
  const items = Array.from(seen.values()).sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  return (
    <div className="admin-card" data-testid="resource-intelligence-tile">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-altura-gold-dark" />
          <div className="font-editorial text-xl text-altura-espresso">Resource Intelligence</div>
        </div>
        <span className="text-xs text-altura-charcoal/50">{items.length} downloaded</span>
      </div>
      {items.length === 0 ? (
        <div className="mt-4 rounded bg-altura-ivory-2 p-4 text-xs text-altura-charcoal/50" data-testid="resource-intelligence-empty">
          No library downloads recorded yet. When this lead requests a resource, it will appear here.
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="resource-intelligence-items">
          {items.map((it) => (
            <span key={it.key}
                  className="inline-flex items-center gap-2 rounded-full border border-altura-gold/30 bg-altura-gold/5 px-3 py-1.5 text-xs text-altura-espresso"
                  title={it.at ? `Downloaded ${new Date(it.at).toLocaleString()}` : "Tagged"}>
              <FileText className="h-3 w-3 text-altura-gold-dark" />
              {it.title}
              {it.count > 1 && <span className="ml-1 rounded-full bg-altura-gold-dark text-altura-ivory px-1.5 text-[10px]">×{it.count}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
